@if (!isExpanded) {
  <ng-container [ngTemplateOutlet]="grid"></ng-container>
} @else {
  <app-split-pane storageKey="split.home" [initialLeftRatio]="0.6" [collapseBreakpointPx]="1024" [collapsedOnSmallScreens]="true">
    <ng-container left>
      <ng-container [ngTemplateOutlet]="grid"></ng-container>
    </ng-container>
    <ng-container right>
      <div class="container py-3 ms-2 home-details">
        <div class="d-flex justify-content-between align-items-center section-heading">
          <div class="d-flex align-items-center gap-2">
            <span class="pill"></span>
            <h4 class="m-0">Detailed Overview: {{ selectedSectionTitle }}</h4>
          </div>
          <button class="btn btn-sm btn-outline-secondary" (click)="isExpanded = false" aria-label="Close details">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="card">
          <div class="card-body">
            @if (selectedSectionKey === 'repos') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  class="badge"
                  [class.bg-success]="reposSummary.githubConnected"
                  [class.bg-warning]="!reposSummary.githubConnected"
                  [class.text-dark]="!reposSummary.githubConnected"
                >
                  GitHub {{ reposSummary.githubConnected ? 'connected' : 'not connected' }}
                </span>
                <span class="badge bg-secondary">Repos: {{ reposSummary.repoCount }}</span>
                <span class="badge bg-secondary">Open PRs: {{ reposSummary.openPRs }}</span>
              </div>
              <p class="text-muted mb-0">
                @if (reposSummary.reposLabel !== '—') {
                  Connected repos: {{ reposSummary.reposLabel }}.
                } @else {
                  Connect project repos to view repo activity.
                }
                Last refresh: {{ reposSummary.lastRefreshedLabel }}.
              </p>
            } @else if (selectedSectionKey === 'overview') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                @if (defineStatus; as ds) {
                  <span class="badge" [class.bg-success]="ds.ready" [class.bg-warning]="!ds.ready">
                    Define: {{ ds.ready ? 'Ready' : 'Needs info' }}
                  </span>
                }
                @if (dbDesignStatus; as dbs) {
                  <span class="badge" [class.bg-success]="dbs.hasData" [class.bg-secondary]="!dbs.hasData">
                    DB: {{ dbs.hasData ? 'Saved' : 'Missing' }}
                  </span>
                }
                @if (ticketsStatus; as ts) {
                  <span class="badge" [class.bg-success]="ts.hasData" [class.bg-secondary]="!ts.hasData">
                    Tickets: {{ ts.hasData ? 'Saved' : 'Missing' }}
                  </span>
                }
              </div>
              <p class="text-muted mb-0">Project: {{ currentProjectName }} • Organization: {{ currentOrganizationName }}</p>
              <div class="mt-3">
                <button class="btn btn-sm btn-outline-primary" type="button" (click)="resyncFromRepo()">
                  <i class="bi bi-arrow-repeat me-1"></i>
                  Re-sync from repo
                </button>
                <div class="small text-muted mt-1">
                  Tip: after syncing Define, repeat on DB Design and Tickets to keep the summaries current.
                </div>
              </div>
            } @else if (selectedSectionKey === 'notifications') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span class="badge bg-warning text-dark">Unread: {{ notificationsSummary.unread }}</span>
                <span class="badge bg-secondary">Mentions: {{ notificationsSummary.mentions }}</span>
                <span class="badge bg-secondary">System: {{ notificationsSummary.system }}</span>
                <span class="badge bg-secondary">Invites: {{ notificationsSummary.invites }}</span>
              </div>
              <p class="text-muted mb-0">
                Notifications are now summarised from the live notification feed, including mentions, system alerts, and pending invites.
              </p>
            } @else if (selectedSectionKey === 'devops') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span class="badge" [class.bg-success]="devopsSummary.authenticated" [class.bg-secondary]="!devopsSummary.authenticated">
                  {{ devopsSummary.authenticated ? 'Signed in' : 'Not signed in' }}
                </span>
                @if (devopsSummary.authenticated) {
                  <span class="badge bg-secondary">Team: {{ devopsSummary.teamName }}</span>
                  <span class="badge bg-secondary">Spaces: {{ devopsSummary.spaceCount }}</span>
                  <span class="badge bg-secondary">Capsules: {{ devopsSummary.capsuleCount }}</span>
                }
              </div>
              <p class="text-muted mb-0">
                @if (devopsSummary.authenticated) {
                  Showing Code Capsules visibility for the matched team. Last overview update: {{ devopsSummary.lastUpdatedLabel }}.
                } @else {
                  Sign in to Code Capsules on the DevOps page to surface deployment details.
                }
              </p>
            } @else if (selectedSectionKey === 'workspace') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span class="badge bg-secondary">Projects: {{ workspaceSummary.projects }}</span>
                <span class="badge bg-secondary">Organizations: {{ workspaceSummary.organizations }}</span>
                <span class="badge bg-secondary">Linked projects: {{ workspaceSummary.linkedProjects }}</span>
              </div>
              <p class="text-muted mb-0">
                Workspace now surfaces project-to-organization coverage so you can spot unassigned projects and switch contexts faster.
              </p>
            } @else if (selectedSectionKey === 'quotations') {
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span class="badge bg-secondary">Total quotes: {{ quotationsSummary.total }}</span>
                <span class="badge bg-secondary">Ready to send: {{ quotationsSummary.readyToSend }}</span>
                <span class="badge bg-secondary">History: {{ quotationsSummary.history }}</span>
              </div>
              @if (quotationsLoading) {
                <p class="text-muted mb-0">Loading quotation activity for the selected project...</p>
              } @else if (quotationsError) {
                <p class="text-muted mb-0">{{ quotationsError }}</p>
              } @else {
                <p class="text-muted mb-0">Last quotation update: {{ quotationsSummary.lastUpdatedLabel }}.</p>
              }
            } @else {
              @if (selectedWorkflowStatus; as s) {
                <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span class="badge" [class.bg-success]="s.ready" [class.bg-warning]="!s.ready">
                    {{ s.ready ? 'Ready' : 'Needs work' }}
                  </span>
                  <span class="badge bg-secondary">{{ s.hasData ? 'Has data' : 'No data yet' }}</span>
                </div>
                @if (s.issues.length > 0) {
                  <div class="fw-500 mb-1">Issues</div>
                  <ul class="small text-muted mb-0">
                    @for (issue of s.issues; track issue) {
                      <li>{{ issue }}</li>
                    }
                  </ul>
                } @else {
                  <p class="text-muted mb-0">No issues detected for this section.</p>
                }
              } @else {
                <p class="text-muted mb-0">Detailed overview is available for this section from the summary cards below.</p>
              }
            }
          </div>
        </div>

        @switch (selectedSectionKey) {
          @case ('overview') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="fw-semibold mb-1">Quick links</div>
                <div class="small text-muted mb-3">Jump straight into the active sections for this project and organization.</div>
                <div class="d-flex flex-wrap gap-2">
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/define']"><i class="bi bi-pencil-square me-1"></i>Define</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/db-design']"><i class="bi bi-database me-1"></i>DB Design</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/repos']"><i class="bi bi-github me-1"></i>Repos</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/tickets']"><i class="bi bi-ticket-perforated me-1"></i>Tickets</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/devops']"><i class="bi bi-gear me-1"></i>DevOps</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/workspace']"><i class="bi bi-collection me-1"></i>Workspace</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/users']"><i class="bi bi-people me-1"></i>Users</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/contact']"><i class="bi bi-envelope me-1"></i>Contact</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/quotations']"><i class="bi bi-file-earmark-text me-1"></i>Quotations</a>
                  <a class="btn btn-sm btn-outline-primary" [routerLink]="['/notifications']"><i class="bi bi-bell me-1"></i>Notifications</a>
                </div>
              </div>
            </div>
          }
          @case ('define') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Goals:</span> {{ defineSummary.readiness.counts.goals }}/1</div>
                  <div><span class="fw-semibold">Features:</span> {{ defineSummary.readiness.counts.features }}/5</div>
                  <div><span class="fw-semibold">In scope:</span> {{ defineSummary.readiness.counts.inScope }}/3</div>
                </div>
                <div class="row g-3">
                  <div class="col-12 col-lg-6">
                    <app-chartjs type="doughnut" [data]="defineChecklistChartData" [options]="doughnutOptions" [heightPx]="220"></app-chartjs>
                  </div>
                  <div class="col-12 col-lg-6">
                    <app-chartjs type="bar" [data]="defineTargetsChartData" [options]="defaultChartOptions" [heightPx]="220"></app-chartjs>
                  </div>
                </div>
              </div>
            </div>
          }
          @case ('db-design') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Tables:</span> {{ dbDesignSummary.tables }}</div>
                  <div><span class="fw-semibold">Relationships:</span> {{ dbDesignSummary.relationships }}</div>
                </div>
                <div class="row g-3">
                  <div class="col-12 col-lg-7">
                    <app-chartjs type="bar" [data]="dbTopTablesChartData" [options]="defaultChartOptions" [heightPx]="240"></app-chartjs>
                    <div class="small text-muted mt-1">Top tables by field count</div>
                  </div>
                  <div class="col-12 col-lg-5">
                    <app-chartjs type="doughnut" [data]="dbFieldTypesChartData" [options]="doughnutOptions" [heightPx]="240"></app-chartjs>
                    <div class="small text-muted mt-1">PK / FK / Other fields</div>
                  </div>
                </div>
              </div>
            </div>
          }
          @case ('repos') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">GitHub:</span> {{ reposSummary.githubConnected ? 'Connected' : 'Not connected' }}</div>
                  <div><span class="fw-semibold">Repos:</span> {{ reposSummary.repoCount }}</div>
                  <div><span class="fw-semibold">Open PRs:</span> {{ reposSummary.openPRs }}</div>
                </div>
                <div class="small text-muted mb-1">
                  @if (reposSummary.reposLabel !== '—') {
                    Connected repos: {{ reposSummary.reposLabel }}
                  } @else {
                    No repos connected yet.
                  }
                </div>
                <div class="small text-muted mb-3">
                  Last refresh: {{ reposSummary.lastRefreshedLabel }}
                </div>
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/repos']">
                  <i class="bi bi-github me-1"></i>
                  Open Repos
                </a>
              </div>
            </div>
          }
          @case ('tickets') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Total cards:</span> {{ ticketsSummary.totalCards }}</div>
                  <div><span class="fw-semibold">Done:</span> {{ ticketsSummary.doneCards }}</div>
                  <div><span class="fw-semibold">Not done:</span> {{ ticketsSummary.notDoneCards }}</div>
                </div>
                <div class="row g-3">
                  <div class="col-12 col-lg-7">
                    <app-chartjs type="bar" [data]="ticketsByColumnChartData" [options]="defaultChartOptions" [heightPx]="240"></app-chartjs>
                    <div class="small text-muted mt-1">Cards by column</div>
                  </div>
                  <div class="col-12 col-lg-5">
                    <app-chartjs type="doughnut" [data]="ticketsDoneChartData" [options]="doughnutOptions" [heightPx]="240"></app-chartjs>
                    <div class="small text-muted mt-1">Done vs not done</div>
                  </div>
                </div>
              </div>
            </div>
          }
          @case ('devops') {
            <div class="card mt-3">
              <div class="card-body">
                @if (!devopsSummary.authenticated) {
                  <p class="text-muted mb-3">
                    Sign in to Code Capsules on the DevOps page to see the matched team, spaces, and capsules for this project.
                  </p>
                } @else {
                  <div class="d-flex flex-wrap gap-3 mb-3">
                    <div><span class="fw-semibold">Signed in:</span> {{ devopsSummary.email }}</div>
                    <div><span class="fw-semibold">Team:</span> {{ devopsSummary.teamName }}</div>
                    <div><span class="fw-semibold">Match:</span> {{ devopsSummary.matchSource }}</div>
                  </div>
                  <div class="d-flex flex-wrap gap-3 mb-3">
                    <div><span class="fw-semibold">Spaces:</span> {{ devopsSummary.spaceCount }}</div>
                    <div><span class="fw-semibold">Capsules:</span> {{ devopsSummary.capsuleCount }}</div>
                  </div>

                  @if (devopsSummary.environments.length > 0) {
                    <div class="fw-semibold small mb-2">By environment</div>
                    <div class="row g-2 mb-3">
                      @for (env of devopsSummary.environments; track env.environment) {
                        <div class="col-6 col-md-3">
                          <div class="card h-100">
                            <div class="card-body p-2">
                              <div class="small fw-semibold">{{ env.label }}</div>
                              <div class="small text-muted">
                                {{ env.spaceCount }} space{{ env.spaceCount === 1 ? '' : 's' }}
                              </div>
                              <div class="small text-muted">
                                {{ env.capsuleCount }} capsule{{ env.capsuleCount === 1 ? '' : 's' }}
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }

                  @if (devopsSummary.spaces.length > 0) {
                    <div class="fw-semibold small mb-2">Spaces</div>
                    <ul class="list-unstyled small mb-3">
                      @for (space of devopsSummary.spaces; track space.slug) {
                        <li>
                          <span class="fw-semibold">{{ space.name }}</span>
                          @if (space.environment) {
                            <span class="text-muted"> · {{ space.environment }}</span>
                          }
                          @if (space.region) {
                            <span class="text-muted"> · {{ space.region }}</span>
                          }
                          <span class="text-muted"> · {{ space.capsuleCount }} capsule{{ space.capsuleCount === 1 ? '' : 's' }}</span>
                          @if (space.matchedRepoCount > 0) {
                            <span class="badge bg-success ms-2">{{ space.matchedRepoCount }} repo match{{ space.matchedRepoCount === 1 ? '' : 'es' }}</span>
                          }
                        </li>
                      }
                    </ul>
                  }

                  <div class="small text-muted mb-3">Last overview update: {{ devopsSummary.lastUpdatedLabel }}.</div>
                }
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/devops']">
                  <i class="bi bi-gear me-1"></i>
                  Open DevOps
                </a>
              </div>
            </div>
          }
          @case ('workspace') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Projects:</span> {{ workspaceSummary.projects }}</div>
                  <div><span class="fw-semibold">Organizations:</span> {{ workspaceSummary.organizations }}</div>
                  <div><span class="fw-semibold">Unassigned:</span> {{ workspaceSummary.unassignedProjects }}</div>
                </div>
                <div class="small text-muted mb-3">
                  Current project: {{ currentProjectName }}. Current organization: {{ currentOrganizationName }}.
                </div>
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/workspace']">
                  <i class="bi bi-collection me-1"></i>
                  Open Workspace
                </a>
              </div>
            </div>
          }
          @case ('users') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Project members:</span> {{ usersSummary.projectMembers }}</div>
                  <div><span class="fw-semibold">Org members:</span> {{ usersSummary.organizationMembers }}</div>
                  <div><span class="fw-semibold">Pending invites:</span> {{ usersSummary.pendingInvites }}</div>
                </div>
                @if (usersSummary.recent.length > 0) {
                  <ul class="list-unstyled small mb-3">
                    @for (user of usersSummary.recent; track (user.label + '-' + user.meta)) {
                      <li>{{ user.label }} — <span class="text-muted">{{ user.meta }}</span></li>
                    }
                  </ul>
                } @else {
                  <div class="small text-muted mb-3">No members loaded for the current project or organization yet.</div>
                }
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/users']">
                  <i class="bi bi-people me-1"></i>
                  Open Users
                </a>
              </div>
            </div>
          }
          @case ('contact') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Support topics:</span> {{ contactSummary.supportTopics }}</div>
                  <div><span class="fw-semibold">Upcoming features:</span> {{ contactSummary.upcomingFeatures }}</div>
                </div>
                <ul class="small text-muted mb-3">
                  <li>Project context attached: {{ contactSummary.hasProjectContext ? 'Yes' : 'No' }}</li>
                  <li>Organization context attached: {{ contactSummary.hasOrganizationContext ? 'Yes' : 'No' }}</li>
                  <li>Topics covered include support, sales, billing, technical issues, and feature requests.</li>
                </ul>
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/contact']">
                  <i class="bi bi-envelope me-1"></i>
                  Open Contact
                </a>
              </div>
            </div>
          }
          @case ('quotations') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Drafts:</span> {{ quotationsSummary.draft }}</div>
                  <div><span class="fw-semibold">Ready:</span> {{ quotationsSummary.readyToSend }}</div>
                  <div><span class="fw-semibold">Sent:</span> {{ quotationsSummary.sent }}</div>
                </div>
                @if (quotationsLoading) {
                  <div class="small text-muted mb-3">Loading quotation history...</div>
                } @else if (quotationsError) {
                  <div class="small text-muted mb-3">{{ quotationsError }}</div>
                } @else if (quotationsSummary.recentNames.length > 0) {
                  <ul class="small text-muted mb-3">
                    @for (name of quotationsSummary.recentNames; track name) {
                      <li>{{ name }}</li>
                    }
                  </ul>
                } @else {
                  <div class="small text-muted mb-3">No quotations have been generated for the selected project yet.</div>
                }
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/quotations']">
                  <i class="bi bi-file-earmark-text me-1"></i>
                  Open Quotations
                </a>
              </div>
            </div>
          }
          @case ('notifications') {
            <div class="card mt-3">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3 mb-2">
                  <div><span class="fw-semibold">Unread:</span> {{ notificationsSummary.unread }}</div>
                  <div><span class="fw-semibold">Mentions:</span> {{ notificationsSummary.mentions }}</div>
                  <div><span class="fw-semibold">Pending invites:</span> {{ notificationsSummary.invites }}</div>
                </div>
                @if (notificationsSummary.recent.length > 0) {
                  <ul class="list-unstyled small mb-3">
                    @for (notification of notificationsSummary.recent; track (notification.title + '-' + notification.when)) {
                      <li>{{ notification.title }} — <span class="text-muted">{{ notification.when }}</span></li>
                    }
                  </ul>
                } @else {
                  <div class="small text-muted mb-3">No live notifications yet for this account.</div>
                }
                @if (notificationsSummary.inviteRows.length > 0) {
                  <div class="small fw-semibold mb-1">Pending invites</div>
                  <ul class="small text-muted mb-3">
                    @for (invite of notificationsSummary.inviteRows; track invite.id) {
                      <li>{{ invite.type | titlecase }}: {{ invite.name }} — {{ invite.timestamp }}</li>
                    }
                  </ul>
                }
                <a class="btn btn-sm btn-outline-primary" [routerLink]="['/notifications']">
                  <i class="bi bi-bell me-1"></i>
                  Open Notifications
                </a>
              </div>
            </div>
          }
        }
      </div>
    </ng-container>
  </app-split-pane>
}

<ng-template #grid>
  <div class="container py-3 home-grid" [class.grid-mode]="isExpanded">
    <div class="section-heading">
      <span class="pill"></span>
      <h4 class="m-0">Project: {{ currentProjectName }}</h4>
      <button
        class="btn btn-sm btn-outline-secondary ms-auto"
        (click)="collapseProjects = !collapseProjects"
        [attr.aria-label]="collapseProjects ? 'Expand all project cards' : 'Collapse all project cards'"
        [title]="collapseProjects ? 'Expand all' : 'Collapse all'"
      >
        <i class="bi" [ngClass]="collapseProjects ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
      </button>
    </div>
    <div class="row g-3" [class.section-collapsed]="collapseProjects">
      @for (card of projectHomeCards; track card.key) {
        <div class="col-12 col-md-6 col-lg-4">
          <div class="card h-100">
            <div class="card-header d-flex justify-content-between align-items-center">
              <span class="fw-semibold">{{ card.title }}</span>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary" (click)="openSectionDetail(card.key)">See more</button>
                <button class="btn btn-sm btn-outline-primary" (click)="openHomeRoute(card)" [attr.aria-label]="'View ' + card.title">
                  <i class="bi bi-eye"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              @switch (card.key) {
                @case ('overview') {
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    @if (defineStatus; as ds) {
                      <span class="badge" [class.bg-success]="ds.ready" [class.bg-warning]="!ds.ready">
                        Define: {{ ds.ready ? 'Ready' : 'Needs info' }}
                      </span>
                    }
                    @if (dbDesignStatus; as dbs) {
                      <span class="badge" [class.bg-success]="dbs.hasData" [class.bg-secondary]="!dbs.hasData">
                        DB: {{ dbs.hasData ? 'Saved' : 'Missing' }}
                      </span>
                    }
                    @if (ticketsStatus; as ts) {
                      <span class="badge" [class.bg-success]="ts.hasData" [class.bg-secondary]="!ts.hasData">
                        Tickets: {{ ts.hasData ? 'Saved' : 'Missing' }}
                      </span>
                    }
                    <span class="badge bg-info text-dark">Org: {{ currentOrganizationName }}</span>
                  </div>
                  <div class="small text-muted">Last updated {{ overview.lastUpdated }}</div>
                }
                @case ('define') {
                  @if (defineStatus; as status) {
                    <div class="mb-2">
                      <span class="badge" [class.bg-success]="status.ready" [class.bg-warning]="!status.ready">
                        {{ status.ready ? 'Ready' : 'Needs info' }}
                      </span>
                    </div>
                  }
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Goals:</span> {{ defineSummary.readiness.counts.goals }}/1</div>
                    <div><span class="fw-semibold">Features:</span> {{ defineSummary.readiness.counts.features }}/5</div>
                    <div><span class="fw-semibold">In scope:</span> {{ defineSummary.readiness.counts.inScope }}/3</div>
                  </div>
                  @if (!defineSummary.readiness.ready && defineSummary.readiness.missing.length > 0) {
                    <div class="small text-muted">Next: {{ defineSummary.readiness.missing[0] }}</div>
                  } @else {
                    <div class="small text-muted">Definition looks good — ready to move on.</div>
                  }
                }
                @case ('db-design') {
                  @if (dbDesignStatus; as status) {
                    <div class="mb-2">
                      <span class="badge" [class.bg-success]="status.hasData" [class.bg-secondary]="!status.hasData">
                        {{ status.hasData ? 'Schema saved' : 'No schema yet' }}
                      </span>
                    </div>
                  }
                  <div class="d-flex flex-wrap gap-3">
                    <div><span class="fw-semibold">Tables:</span> {{ dbDesignSummary.tables }}</div>
                    <div><span class="fw-semibold">Rels:</span> {{ dbDesignSummary.relationships }}</div>
                  </div>
                  @if (dbDesignSummary.topTables.length > 0) {
                    <div class="small text-muted mt-2">Largest: {{ dbDesignSummary.topTables[0].name }} ({{ dbDesignSummary.topTables[0].fields }} fields)</div>
                  } @else {
                    <div class="small text-muted mt-2">Generate and save a schema to see metrics here.</div>
                  }
                }
                @case ('repos') {
                  <div class="mb-2">
                    <span class="badge" [class.bg-success]="reposSummary.githubConnected" [class.bg-warning]="!reposSummary.githubConnected" [class.text-dark]="!reposSummary.githubConnected">
                      GitHub: {{ reposSummary.githubConnected ? 'Connected' : 'Not connected' }}
                    </span>
                  </div>
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Repos:</span> {{ reposSummary.repoCount }}</div>
                    <div><span class="fw-semibold">Open PRs:</span> {{ reposSummary.openPRs }}</div>
                  </div>
                  <div class="small text-muted">
                    @if (reposSummary.reposLabel !== '—') {
                      Connected: {{ reposSummary.reposLabel }}
                    } @else {
                      Connect repos to see activity.
                    }
                  </div>
                  <div class="small text-muted mt-1">Last refresh: {{ reposSummary.lastRefreshedLabel }}</div>
                }
                @case ('tickets') {
                  @if (ticketsStatus; as status) {
                    <div class="mb-2">
                      <span class="badge" [class.bg-success]="status.hasData" [class.bg-secondary]="!status.hasData">
                        {{ status.hasData ? 'Board saved' : 'No saved board yet' }}
                      </span>
                    </div>
                  }
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Total:</span> {{ ticketsSummary.totalCards }}</div>
                    <div><span class="fw-semibold">Done:</span> {{ ticketsSummary.doneCards }}</div>
                  </div>
                  @if (ticketsSummary.byColumn.length > 0) {
                    <ul class="list-unstyled small mb-0">
                      @for (column of ticketsSummary.byColumn; track column.title) {
                        <li>{{ column.title }} • <span class="fw-semibold">{{ column.count }}</span></li>
                      }
                    </ul>
                  } @else {
                    <div class="small text-muted">Save a board on Tickets to populate this summary.</div>
                  }
                }
                @case ('devops') {
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Auth:</span> {{ devopsSummary.authenticated ? 'Connected' : 'Not connected' }}</div>
                    @if (devopsSummary.authenticated) {
                      <div><span class="fw-semibold">Spaces:</span> {{ devopsSummary.spaceCount }}</div>
                      <div><span class="fw-semibold">Capsules:</span> {{ devopsSummary.capsuleCount }}</div>
                    }
                  </div>
                  @if (devopsSummary.authenticated) {
                    <div class="small text-muted">Team: {{ devopsSummary.teamName }}</div>
                  }
                  <div class="small text-muted">Last overview update: {{ devopsSummary.lastUpdatedLabel }}</div>
                }
                @case ('workspace') {
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Projects:</span> {{ workspaceSummary.projects }}</div>
                    <div><span class="fw-semibold">Organizations:</span> {{ workspaceSummary.organizations }}</div>
                  </div>
                  <div class="small text-muted">Unassigned projects: {{ workspaceSummary.unassignedProjects }}</div>
                }
                @case ('quotations') {
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Drafts:</span> {{ quotationsSummary.draft }}</div>
                    <div><span class="fw-semibold">Ready:</span> {{ quotationsSummary.readyToSend }}</div>
                  </div>
                  @if (quotationsLoading) {
                    <div class="small text-muted">Loading quotation activity...</div>
                  } @else if (quotationsError) {
                    <div class="small text-muted">{{ quotationsError }}</div>
                  } @else {
                    <div class="small text-muted">Last update: {{ quotationsSummary.lastUpdatedLabel }}</div>
                  }
                }
              }
              <div class="small mt-2">
                <i class="bi bi-robot me-1"></i>
                <span class="text-muted">Agent interactions (7d):</span>
                {{ agentCounts[card.key]?.last7d ?? 0 }}
              </div>
            </div>
          </div>
        </div>
      }
    </div>

    <hr class="section-divider" />

    <div class="section-heading">
      <span class="pill"></span>
      <h4 class="m-0">Organization: {{ currentOrganizationName }}</h4>
      <button
        class="btn btn-sm btn-outline-secondary ms-auto"
        (click)="collapseOrganizations = !collapseOrganizations"
        [attr.aria-label]="collapseOrganizations ? 'Expand all organization cards' : 'Collapse all organization cards'"
        [title]="collapseOrganizations ? 'Expand all' : 'Collapse all'"
      >
        <i class="bi" [ngClass]="collapseOrganizations ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
      </button>
    </div>
    <div class="row g-3" [class.section-collapsed]="collapseOrganizations">
      @for (card of organizationHomeCards; track card.key) {
        <div class="col-12 col-md-6 col-lg-4">
          <div class="card h-100">
            <div class="card-header d-flex justify-content-between align-items-center">
              <span class="fw-semibold">{{ card.title }}</span>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary" (click)="openSectionDetail(card.key)">See more</button>
                <button class="btn btn-sm btn-outline-primary" (click)="openHomeRoute(card)" [attr.aria-label]="'View ' + card.title">
                  <i class="bi bi-eye"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              @switch (card.key) {
                @case ('users') {
                  <div class="d-flex flex-wrap gap-3 mb-3">
                    <div><span class="fw-semibold">Project members:</span> {{ usersSummary.projectMembers }}</div>
                    <div><span class="fw-semibold">Org members:</span> {{ usersSummary.organizationMembers }}</div>
                    <div><span class="fw-semibold">Invites:</span> {{ usersSummary.pendingInvites }}</div>
                  </div>
                  @if (usersSummary.recent.length > 0) {
                    <ul class="list-unstyled small mb-0">
                      @for (user of usersSummary.recent; track (user.label + '-' + user.meta)) {
                        <li>{{ user.label }} — <span class="text-muted">{{ user.meta }}</span></li>
                      }
                    </ul>
                  } @else {
                    <div class="small text-muted">No members loaded for the current project or organization yet.</div>
                  }
                }
                @case ('contact') {
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Topics:</span> {{ contactSummary.supportTopics }}</div>
                    <div><span class="fw-semibold">Upcoming:</span> {{ contactSummary.upcomingFeatures }}</div>
                  </div>
                  <div class="small text-muted">
                    Contact forms will include {{ contactSummary.hasProjectContext ? 'project' : 'no project' }} and
                    {{ contactSummary.hasOrganizationContext ? 'organization' : 'no organization' }} context.
                  </div>
                }
                @case ('notifications') {
                  <div class="d-flex flex-wrap gap-3 mb-2">
                    <div><span class="fw-semibold">Unread:</span> {{ notificationsSummary.unread }}</div>
                    <div><span class="fw-semibold">Invites:</span> {{ notificationsSummary.invites }}</div>
                  </div>
                  @if (notificationsSummary.recent.length > 0) {
                    <ul class="list-unstyled small mb-0">
                      @for (notification of notificationsSummary.recent; track (notification.title + '-' + notification.when)) {
                        <li>{{ notification.title }} — <span class="text-muted">{{ notification.when }}</span></li>
                      }
                    </ul>
                  } @else {
                    <div class="small text-muted">No live notifications yet for this account.</div>
                  }
                }
              }
              <div class="small mt-2">
                <i class="bi bi-robot me-1"></i>
                <span class="text-muted">Agent interactions (7d):</span>
                {{ agentCounts[card.key]?.last7d ?? 0 }}
              </div>
            </div>
          </div>
        </div>
      }
    </div>

    <div class="home-shortcuts-fab" [class.is-expanded]="shortcutFabExpanded">
      <div class="home-shortcuts-actions" aria-label="Create shortcuts">
        <button type="button" class="btn btn-light home-shortcuts-action" (click)="goToCreateOrganization()">
          <i class="bi bi-building-add" aria-hidden="true"></i>
          <span>Create Organization</span>
        </button>
        <button type="button" class="btn btn-light home-shortcuts-action" (click)="goToCreateProject()">
          <i class="bi bi-folder-plus" aria-hidden="true"></i>
          <span>Create Project</span>
        </button>
      </div>

      <button
        type="button"
        class="btn btn-primary home-shortcuts-trigger"
        (click)="toggleShortcutFab()"
        [attr.aria-expanded]="shortcutFabExpanded"
        [attr.aria-label]="shortcutFabExpanded ? 'Hide create shortcuts' : 'Show create shortcuts'"
      >
        <i class="bi" [ngClass]="shortcutFabExpanded ? 'bi-x-lg' : 'bi-plus-lg'" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</ng-template>
